import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plug, Plus, Edit, Trash2, Search, Loader2, FileText } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { modelosCarregadoresService } from "@/services/modelosCarregadoresService";
import {
  CreateModeloCarregadorVeicular,
  ModeloCarregadorVeicular,
  TIPOS_CORRENTE_CARREGADOR,
  TipoCorrenteCarregador,
  labelTipoCorrenteCarregador,
  nomeExibicaoModeloCarregador,
} from "@/types/modelosCarregadores";
import { toast } from "sonner";

type FormValues = {
  marca: string;
  modelo: string;
  codigo: string;
  tipoCorrente: TipoCorrenteCarregador;
  modoRecarga: string;
  potenciaNominalKw: string;
  potenciaNominalTexto: string;
  tensaoEntrada: string;
  correnteEntrada: string;
  frequencia: string;
  conexaoEletrica: string;
  tensaoSaida: string;
  correnteSaida: string;
  fatorPotencia: string;
  eficiencia: string;
  tipoConector: string;
  quantidadeConectores: string;
  comprimentoCabo: string;
  interfaceUsuario: string;
  modoInicio: string;
  comunicacao: string;
  protocolo: string;
  medidorEnergia: string;
  grauProtecao: string;
  protecaoImpacto: string;
  protecoesEletricas: string;
  normas: string;
  temperaturaOperacao: string;
  umidadeOperacao: string;
  altitudeOperacao: string;
  dimensoes: string;
  pesoKg: string;
  materialInvolucro: string;
  instalacao: string;
  garantiaMeses: string;
  observacoes: string;
  datasheetUrl: string | null;
  imagemUrl: string | null;
  imagemUrl2: string | null;
};

function vazio(valor: string): string | null {
  const texto = valor.trim();
  return texto ? texto : null;
}

function numeroOpcional(valor: string): number | null {
  if (!valor.trim()) return null;
  const n = Number(valor.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function emptyForm(): FormValues {
  return {
    marca: "",
    modelo: "",
    codigo: "",
    tipoCorrente: "CA",
    modoRecarga: "",
    potenciaNominalKw: "",
    potenciaNominalTexto: "",
    tensaoEntrada: "",
    correnteEntrada: "",
    frequencia: "",
    conexaoEletrica: "",
    tensaoSaida: "",
    correnteSaida: "",
    fatorPotencia: "",
    eficiencia: "",
    tipoConector: "",
    quantidadeConectores: "",
    comprimentoCabo: "",
    interfaceUsuario: "",
    modoInicio: "",
    comunicacao: "",
    protocolo: "",
    medidorEnergia: "",
    grauProtecao: "",
    protecaoImpacto: "",
    protecoesEletricas: "",
    normas: "",
    temperaturaOperacao: "",
    umidadeOperacao: "",
    altitudeOperacao: "",
    dimensoes: "",
    pesoKg: "",
    materialInvolucro: "",
    instalacao: "",
    garantiaMeses: "",
    observacoes: "",
    datasheetUrl: null,
    imagemUrl: null,
    imagemUrl2: null,
  };
}

function modeloParaForm(item: ModeloCarregadorVeicular): FormValues {
  return {
    marca: item.marca,
    modelo: item.modelo,
    codigo: item.codigo ?? "",
    tipoCorrente: item.tipoCorrente,
    modoRecarga: item.modoRecarga ?? "",
    potenciaNominalKw: String(item.potenciaNominalKw),
    potenciaNominalTexto: item.potenciaNominalTexto ?? "",
    tensaoEntrada: item.tensaoEntrada ?? "",
    correnteEntrada: item.correnteEntrada ?? "",
    frequencia: item.frequencia ?? "",
    conexaoEletrica: item.conexaoEletrica ?? "",
    tensaoSaida: item.tensaoSaida ?? "",
    correnteSaida: item.correnteSaida ?? "",
    fatorPotencia: item.fatorPotencia ?? "",
    eficiencia: item.eficiencia ?? "",
    tipoConector: item.tipoConector ?? "",
    quantidadeConectores: item.quantidadeConectores != null ? String(item.quantidadeConectores) : "",
    comprimentoCabo: item.comprimentoCabo ?? "",
    interfaceUsuario: item.interfaceUsuario ?? "",
    modoInicio: item.modoInicio ?? "",
    comunicacao: item.comunicacao ?? "",
    protocolo: item.protocolo ?? "",
    medidorEnergia: item.medidorEnergia ?? "",
    grauProtecao: item.grauProtecao ?? "",
    protecaoImpacto: item.protecaoImpacto ?? "",
    protecoesEletricas: item.protecoesEletricas ?? "",
    normas: item.normas ?? "",
    temperaturaOperacao: item.temperaturaOperacao ?? "",
    umidadeOperacao: item.umidadeOperacao ?? "",
    altitudeOperacao: item.altitudeOperacao ?? "",
    dimensoes: item.dimensoes ?? "",
    pesoKg: item.pesoKg != null ? String(item.pesoKg) : "",
    materialInvolucro: item.materialInvolucro ?? "",
    instalacao: item.instalacao ?? "",
    garantiaMeses: item.garantiaMeses != null ? String(item.garantiaMeses) : "",
    observacoes: item.observacoes ?? "",
    datasheetUrl: item.datasheetUrl,
    imagemUrl: item.imagemUrl,
    imagemUrl2: item.imagemUrl2,
  };
}

function formParaPayload(form: FormValues): CreateModeloCarregadorVeicular {
  const potencia = numeroOpcional(form.potenciaNominalKw);
  return {
    marca: form.marca.trim(),
    modelo: form.modelo.trim(),
    codigo: vazio(form.codigo),
    tipoCorrente: form.tipoCorrente,
    modoRecarga: vazio(form.modoRecarga),
    potenciaNominalKw: potencia ?? 0,
    potenciaNominalTexto: vazio(form.potenciaNominalTexto),
    tensaoEntrada: vazio(form.tensaoEntrada),
    correnteEntrada: vazio(form.correnteEntrada),
    frequencia: vazio(form.frequencia),
    conexaoEletrica: vazio(form.conexaoEletrica),
    tensaoSaida: vazio(form.tensaoSaida),
    correnteSaida: vazio(form.correnteSaida),
    fatorPotencia: vazio(form.fatorPotencia),
    eficiencia: vazio(form.eficiencia),
    tipoConector: vazio(form.tipoConector),
    quantidadeConectores: numeroOpcional(form.quantidadeConectores),
    comprimentoCabo: vazio(form.comprimentoCabo),
    interfaceUsuario: vazio(form.interfaceUsuario),
    modoInicio: vazio(form.modoInicio),
    comunicacao: vazio(form.comunicacao),
    protocolo: vazio(form.protocolo),
    medidorEnergia: vazio(form.medidorEnergia),
    grauProtecao: vazio(form.grauProtecao),
    protecaoImpacto: vazio(form.protecaoImpacto),
    protecoesEletricas: vazio(form.protecoesEletricas),
    normas: vazio(form.normas),
    temperaturaOperacao: vazio(form.temperaturaOperacao),
    umidadeOperacao: vazio(form.umidadeOperacao),
    altitudeOperacao: vazio(form.altitudeOperacao),
    dimensoes: vazio(form.dimensoes),
    pesoKg: numeroOpcional(form.pesoKg),
    materialInvolucro: vazio(form.materialInvolucro),
    instalacao: vazio(form.instalacao),
    garantiaMeses: numeroOpcional(form.garantiaMeses),
    observacoes: vazio(form.observacoes),
    datasheetUrl: form.datasheetUrl,
    imagemUrl: form.imagemUrl,
    imagemUrl2: form.imagemUrl2,
  };
}

function rotuloPotencia(item: ModeloCarregadorVeicular): string {
  if (item.potenciaNominalTexto) return item.potenciaNominalTexto;
  return `${item.potenciaNominalKw.toLocaleString("pt-BR")} kW`;
}

export default function ModelosCarregadores() {
  const permissions = usePermissions();
  const canManage = permissions.userRole === "admin" || permissions.userRole === "vendedor";

  const [itens, setItens] = useState<ModeloCarregadorVeicular[]>([]);
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editando, setEditando] = useState<ModeloCarregadorVeicular | null>(null);
  const [form, setForm] = useState<FormValues>(emptyForm);
  const [imagem1, setImagem1] = useState<File | null>(null);
  const [imagem2, setImagem2] = useState<File | null>(null);
  const [preview1, setPreview1] = useState<string | null>(null);
  const [preview2, setPreview2] = useState<string | null>(null);
  const [datasheet, setDatasheet] = useState<File | null>(null);
  const [excluirId, setExcluirId] = useState<string | null>(null);
  const imagem1Ref = useRef<HTMLInputElement>(null);
  const imagem2Ref = useRef<HTMLInputElement>(null);
  const datasheetRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void carregar();
  }, []);

  const carregar = async () => {
    try {
      setCarregando(true);
      setItens(await modelosCarregadoresService.getAll());
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar os carregadores");
    } finally {
      setCarregando(false);
    }
  };

  const abrirNovo = () => {
    setEditando(null);
    setForm(emptyForm());
    setImagem1(null);
    setImagem2(null);
    setPreview1(null);
    setPreview2(null);
    setDatasheet(null);
    setDialogAberto(true);
  };

  const abrirEdicao = (item: ModeloCarregadorVeicular) => {
    setEditando(item);
    setForm(modeloParaForm(item));
    setImagem1(null);
    setImagem2(null);
    setPreview1(item.imagemUrl);
    setPreview2(item.imagemUrl2);
    setDatasheet(null);
    setDialogAberto(true);
  };

  const definirTexto = (campo: keyof FormValues, valor: string) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const escolherImagem = (slot: 1 | 2, file: File | null) => {
    const preview = file ? URL.createObjectURL(file) : null;
    if (slot === 1) {
      setImagem1(file);
      setPreview1(preview);
      if (!file) setForm((atual) => ({ ...atual, imagemUrl: null }));
      return;
    }
    setImagem2(file);
    setPreview2(preview);
    if (!file) setForm((atual) => ({ ...atual, imagemUrl2: null }));
  };

  const salvar = async () => {
    const potencia = numeroOpcional(form.potenciaNominalKw);
    if (!form.marca.trim() || !form.modelo.trim()) {
      toast.error("Informe marca e modelo");
      return;
    }
    if (potencia == null || potencia <= 0) {
      toast.error("Informe a potência nominal em kW");
      return;
    }

    try {
      setSalvando(true);
      const payload = formParaPayload(form);
      let salvo = editando
        ? await modelosCarregadoresService.update(editando.id, payload)
        : await modelosCarregadoresService.create(payload);

      const arquivos: UpdateArquivos = {};
      if (imagem1) arquivos.imagemUrl = await modelosCarregadoresService.uploadImagem(salvo.id, imagem1, 1);
      if (imagem2) arquivos.imagemUrl2 = await modelosCarregadoresService.uploadImagem(salvo.id, imagem2, 2);
      if (datasheet) {
        arquivos.datasheetUrl = await modelosCarregadoresService.uploadDatasheet(salvo.id, datasheet);
      }
      if (Object.keys(arquivos).length > 0) {
        salvo = await modelosCarregadoresService.update(salvo.id, arquivos);
      }

      toast.success(editando ? "Carregador atualizado" : "Carregador cadastrado");
      setDialogAberto(false);
      await carregar();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Erro ao salvar carregador");
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    if (!excluirId) return;
    try {
      await modelosCarregadoresService.delete(excluirId);
      toast.success("Carregador excluído");
      setExcluirId(null);
      await carregar();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Erro ao excluir carregador");
    }
  };

  const termo = busca.trim().toLowerCase();
  const filtrados = itens.filter((item) => {
    if (!termo) return true;
    const texto = [
      item.marca,
      item.modelo,
      item.codigo,
      item.tipoConector,
      item.tipoCorrente,
      String(item.potenciaNominalKw),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return texto.includes(termo);
  });

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
            <Plug className="h-7 w-7" />
            Carregadores Veiculares
          </h1>
          <p className="text-muted-foreground">
            Catálogo técnico dos modelos usados nas propostas.
          </p>
        </div>
        {canManage && (
          <Button onClick={abrirNovo}>
            <Plus className="mr-2 h-4 w-4" />
            Novo modelo
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Modelos cadastrados</CardTitle>
          <CardDescription>
            As especificações desta lista entram na proposta quando o modelo é selecionado.
          </CardDescription>
          <div className="relative max-w-md pt-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por marca, modelo ou conector"
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          {carregando ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando modelos...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Marca</TableHead>
                    <TableHead>Modelo</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Potência</TableHead>
                    <TableHead>Conector</TableHead>
                    <TableHead>Arquivos</TableHead>
                    {canManage && <TableHead className="text-right">Ações</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtrados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canManage ? 7 : 6} className="text-muted-foreground">
                        Nenhum modelo encontrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtrados.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.marca}</TableCell>
                        <TableCell>{item.modelo}</TableCell>
                        <TableCell>{labelTipoCorrenteCarregador(item.tipoCorrente)}</TableCell>
                        <TableCell>{rotuloPotencia(item)}</TableCell>
                        <TableCell>{item.tipoConector || "—"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {item.imagemUrl && (
                              <img src={item.imagemUrl} alt="" className="h-8 w-8 rounded object-cover" />
                            )}
                            {item.imagemUrl2 && (
                              <img src={item.imagemUrl2} alt="" className="h-8 w-8 rounded object-cover" />
                            )}
                            {item.datasheetUrl && (
                              <a href={item.datasheetUrl} target="_blank" rel="noreferrer" className="text-primary">
                                <FileText className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                        </TableCell>
                        {canManage && (
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => abrirEdicao(item)} aria-label="Editar">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setExcluirId(item.id)}
                              aria-label="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editando ? "Editar carregador" : "Novo carregador"}</DialogTitle>
            <DialogDescription>
              {editando
                ? nomeExibicaoModeloCarregador(editando)
                : "Preencha as especificações conforme o datasheet do fabricante."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <Secao titulo="Identificação">
              <Campo label="Marca *" id="marca" value={form.marca} onChange={(v) => definirTexto("marca", v)} />
              <Campo label="Modelo *" id="modelo" value={form.modelo} onChange={(v) => definirTexto("modelo", v)} />
              <Campo label="Código" id="codigo" value={form.codigo} onChange={(v) => definirTexto("codigo", v)} />
              <div className="space-y-2">
                <Label>Tipo de corrente *</Label>
                <Select
                  value={form.tipoCorrente}
                  onValueChange={(v) =>
                    setForm((atual) => ({ ...atual, tipoCorrente: v as TipoCorrenteCarregador }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CORRENTE_CARREGADOR.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Campo label="Modo de recarga" id="modo" value={form.modoRecarga} onChange={(v) => definirTexto("modoRecarga", v)} placeholder="Modo 3 ou Modo 4" />
              <Campo label="Potência nominal (kW) *" id="potencia" value={form.potenciaNominalKw} onChange={(v) => definirTexto("potenciaNominalKw", v)} />
              <Campo label="Potência conforme datasheet" id="potenciaTexto" value={form.potenciaNominalTexto} onChange={(v) => definirTexto("potenciaNominalTexto", v)} placeholder="Ex.: até 7,68 kW" />
              <Campo label="Garantia (meses)" id="garantia" value={form.garantiaMeses} onChange={(v) => definirTexto("garantiaMeses", v)} />
            </Secao>

            <Secao titulo="Entrada">
              <Campo label="Tensão de entrada" id="tensaoEntrada" value={form.tensaoEntrada} onChange={(v) => definirTexto("tensaoEntrada", v)} />
              <Campo label="Corrente de entrada" id="correnteEntrada" value={form.correnteEntrada} onChange={(v) => definirTexto("correnteEntrada", v)} />
              <Campo label="Frequência" id="frequencia" value={form.frequencia} onChange={(v) => definirTexto("frequencia", v)} />
              <Campo label="Conexão elétrica" id="conexao" value={form.conexaoEletrica} onChange={(v) => definirTexto("conexaoEletrica", v)} />
              <Campo label="Fator de potência" id="fp" value={form.fatorPotencia} onChange={(v) => definirTexto("fatorPotencia", v)} />
            </Secao>

            <Secao titulo="Saída">
              <Campo label="Tensão de saída" id="tensaoSaida" value={form.tensaoSaida} onChange={(v) => definirTexto("tensaoSaida", v)} />
              <Campo label="Corrente de saída" id="correnteSaida" value={form.correnteSaida} onChange={(v) => definirTexto("correnteSaida", v)} />
              <Campo label="Eficiência" id="eficiencia" value={form.eficiencia} onChange={(v) => definirTexto("eficiencia", v)} />
            </Secao>

            <Secao titulo="Conector e interface">
              <Campo label="Tipo de conector" id="conector" value={form.tipoConector} onChange={(v) => definirTexto("tipoConector", v)} />
              <Campo label="Quantidade de conectores" id="qtdConectores" value={form.quantidadeConectores} onChange={(v) => definirTexto("quantidadeConectores", v)} />
              <Campo label="Comprimento do cabo" id="cabo" value={form.comprimentoCabo} onChange={(v) => definirTexto("comprimentoCabo", v)} />
              <Campo label="Interface" id="interface" value={form.interfaceUsuario} onChange={(v) => definirTexto("interfaceUsuario", v)} />
              <Campo label="Modo de início" id="inicio" value={form.modoInicio} onChange={(v) => definirTexto("modoInicio", v)} />
              <Campo label="Comunicação" id="comunicacao" value={form.comunicacao} onChange={(v) => definirTexto("comunicacao", v)} />
              <Campo label="Protocolo" id="protocolo" value={form.protocolo} onChange={(v) => definirTexto("protocolo", v)} />
              <Campo label="Medidor de energia" id="medidor" value={form.medidorEnergia} onChange={(v) => definirTexto("medidorEnergia", v)} />
            </Secao>

            <Secao titulo="Proteção e ambiente">
              <Campo label="Grau de proteção" id="ip" value={form.grauProtecao} onChange={(v) => definirTexto("grauProtecao", v)} placeholder="IP65" />
              <Campo label="Proteção contra impacto" id="ik" value={form.protecaoImpacto} onChange={(v) => definirTexto("protecaoImpacto", v)} placeholder="IK10" />
              <Campo label="Temperatura de operação" id="temp" value={form.temperaturaOperacao} onChange={(v) => definirTexto("temperaturaOperacao", v)} />
              <Campo label="Umidade" id="umidade" value={form.umidadeOperacao} onChange={(v) => definirTexto("umidadeOperacao", v)} />
              <Campo label="Altitude" id="altitude" value={form.altitudeOperacao} onChange={(v) => definirTexto("altitudeOperacao", v)} />
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="protecoes">Proteções elétricas</Label>
                <Textarea id="protecoes" value={form.protecoesEletricas} onChange={(e) => definirTexto("protecoesEletricas", e.target.value)} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="normas">Normas</Label>
                <Textarea id="normas" value={form.normas} onChange={(e) => definirTexto("normas", e.target.value)} />
              </div>
            </Secao>

            <Secao titulo="Características mecânicas">
              <Campo label="Dimensões" id="dimensoes" value={form.dimensoes} onChange={(v) => definirTexto("dimensoes", v)} />
              <Campo label="Peso (kg)" id="peso" value={form.pesoKg} onChange={(v) => definirTexto("pesoKg", v)} />
              <Campo label="Material do invólucro" id="material" value={form.materialInvolucro} onChange={(v) => definirTexto("materialInvolucro", v)} />
              <Campo label="Instalação" id="instalacao" value={form.instalacao} onChange={(v) => definirTexto("instalacao", v)} />
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="obs">Observações</Label>
                <Textarea id="obs" value={form.observacoes} onChange={(e) => definirTexto("observacoes", e.target.value)} />
              </div>
            </Secao>

            <Secao titulo="Imagens e datasheet">
              <ArquivoImagem
                id="imagem1"
                label="Imagem 1"
                inputRef={imagem1Ref}
                preview={preview1}
                onFile={(file) => escolherImagem(1, file)}
                onRemove={() => {
                  if (imagem1Ref.current) imagem1Ref.current.value = "";
                  escolherImagem(1, null);
                }}
              />
              <ArquivoImagem
                id="imagem2"
                label="Imagem 2"
                inputRef={imagem2Ref}
                preview={preview2}
                onFile={(file) => escolherImagem(2, file)}
                onRemove={() => {
                  if (imagem2Ref.current) imagem2Ref.current.value = "";
                  escolherImagem(2, null);
                }}
              />
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="datasheet">Datasheet (PDF)</Label>
                <Input
                  ref={datasheetRef}
                  id="datasheet"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setDatasheet(e.target.files?.[0] ?? null)}
                />
                {form.datasheetUrl && !datasheet && (
                  <a href={form.datasheetUrl} target="_blank" rel="noreferrer" className="text-sm text-primary underline">
                    Abrir datasheet atual
                  </a>
                )}
                {datasheet && <p className="text-sm text-muted-foreground">{datasheet.name}</p>}
              </div>
            </Secao>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogAberto(false)} disabled={salvando}>
                Cancelar
              </Button>
              <Button onClick={() => void salvar()} disabled={salvando}>
                {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!excluirId} onOpenChange={(aberto) => !aberto && setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir modelo?</AlertDialogTitle>
            <AlertDialogDescription>
              O modelo sai do catálogo. Propostas já emitidas permanecem como foram geradas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void excluir()}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type UpdateArquivos = {
  imagemUrl?: string;
  imagemUrl2?: string;
  datasheetUrl?: string;
};

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">{titulo}</h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>
    </section>
  );
}

function Campo({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (valor: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function ArquivoImagem({
  id,
  label,
  inputRef,
  preview,
  onFile,
  onRemove,
}: {
  id: string;
  label: string;
  inputRef: RefObject<HTMLInputElement>;
  preview: string | null;
  onFile: (file: File | null) => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        ref={inputRef}
        id={id}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={(e) => onFile(e.target.files?.[0] ?? null)}
      />
      {preview && (
        <div className="flex items-center gap-3">
          <img src={preview} alt={label} className="h-20 w-20 rounded border object-cover" />
          <Button type="button" variant="outline" size="sm" onClick={onRemove}>
            Remover
          </Button>
        </div>
      )}
    </div>
  );
}
